import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { AlertCircle, Calendar, CheckCircle2, RefreshCw, Banknote } from 'lucide-react';
import { loadPendingInstallments, markOverdueInstallments, payInstallment } from './data.js';
import { friendlyError, rupiah } from './lib/format.js';

const METHODS = [
  { value: 'CASH',        label: 'Tunai' },
  { value: 'QRIS',        label: 'QRIS' },
  { value: 'BANK_TRANSFER', label: 'Transfer bank' },
  { value: 'DEBIT_CARD',  label: 'Kartu debit' },
  { value: 'CREDIT_CARD', label: 'Kartu kredit' },
  { value: 'OTHER',       label: 'Lainnya' },
];

function methodLabel(v) {
  return METHODS.find((m) => m.value === v)?.label || v || '—';
}

function dueStatus(daysOverdue) {
  if (daysOverdue > 0)  return { label: `Terlambat ${daysOverdue}h`, bg: '#fee2e2', color: '#b91c1c', dot: '#ef4444' };
  if (daysOverdue === 0) return { label: 'Jatuh tempo hari ini', bg: '#fef9c3', color: '#854d0e', dot: '#eab308' };
  if (daysOverdue >= -3) return { label: `${-daysOverdue}h lagi`, bg: '#fff7ed', color: '#9a3412', dot: '#f97316' };
  return { label: `${-daysOverdue}h lagi`, bg: '#f0fdf4', color: '#166534', dot: '#22c55e' };
}

function waMessage(inst) {
  const termLabel = inst.installment_number === 1 ? 'DP' : `cicilan ke-${inst.installment_number}`;
  const dueStr    = inst.due_date
    ? new Date(inst.due_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'long', year: 'numeric' })
    : '';
  return encodeURIComponent(
    `Halo ${inst.client_name},\n\n` +
    `Kami mengingatkan bahwa *${termLabel}* atas pembelian paket senilai ` +
    `*${rupiah.format(Number(inst.amount))}* jatuh tempo pada *${dueStr}*.\n\n` +
    `Mohon segera hubungi klinik kami untuk menyelesaikan pembayaran.\n\n` +
    `Terima kasih 🙏`
  );
}

export function PiutangPage() {
  const [rows, setRows]           = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [filter, setFilter]       = useState('ALL');   // ALL | OVERDUE | DUE_SOON | UPCOMING
  const [payingId, setPayingId]   = useState(null);
  const [payMethod, setPayMethod] = useState('CASH');
  const [payRef, setPayRef]       = useState('');
  const [payNotes, setPayNotes]   = useState('');
  const [busyId, setBusyId]       = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      await markOverdueInstallments();
      const data = await loadPendingInstallments();
      setRows(data);
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return rows.filter((r) => {
      const d = Number(r.days_overdue || 0);
      if (filter === 'OVERDUE')   return d > 0;
      if (filter === 'DUE_SOON')  return d >= -3 && d <= 0;
      if (filter === 'UPCOMING')  return d < -3;
      return true;
    });
  }, [rows, filter]);

  const counts = useMemo(() => ({
    ALL:      rows.length,
    OVERDUE:  rows.filter((r) => Number(r.days_overdue) > 0).length,
    DUE_SOON: rows.filter((r) => { const d = Number(r.days_overdue); return d >= -3 && d <= 0; }).length,
    UPCOMING: rows.filter((r) => Number(r.days_overdue) < -3).length,
  }), [rows]);

  const totalPiutang = useMemo(
    () => rows.reduce((s, r) => s + Number(r.amount || 0), 0),
    [rows]
  );

  async function handlePayInstallment(inst) {
    if (!window.confirm(
      `Bayar cicilan ke-${inst.installment_number} (${inst.client_name})\n` +
      `${rupiah.format(Number(inst.amount))} via ${methodLabel(payMethod)}?`
    )) return;

    setBusyId(inst.id);
    setError('');
    try {
      await payInstallment(inst.id, {
        payment_method:    payMethod,
        payment_reference: payRef,
        payment_notes:     payNotes,
      });
      setPayingId(null);
      setPayRef('');
      setPayNotes('');
      await load();
    } catch (e) {
      setError(friendlyError(e));
    } finally {
      setBusyId(null);
    }
  }

  const FILTER_TABS = [
    { key: 'ALL',      label: 'Semua' },
    { key: 'OVERDUE',  label: '🔴 Terlambat' },
    { key: 'DUE_SOON', label: '🟡 Segera' },
    { key: 'UPCOMING', label: '🟢 Mendatang' },
  ];

  return (
    <div className="page-stack">
      <article className="panel table-panel">
        <div className="panel-head" style={{ padding: '1.25rem 1.25rem 0' }}>
          <div>
            <span className="eyebrow">Finance · Kas</span>
            <h3>Piutang Cicilan</h3>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {totalPiutang > 0 ? (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 600, textTransform: 'uppercase' }}>Total Piutang</div>
                <div style={{ fontSize: 20, fontWeight: 800, color: '#b91c1c' }}>{rupiah.format(totalPiutang)}</div>
              </div>
            ) : null}
            <button
              className="button button-soft"
              onClick={load}
              disabled={loading}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              <RefreshCw size={15} className={loading ? 'spin' : ''} />
              Refresh
            </button>
          </div>
        </div>

        {error ? (
          <div className="alert alert-danger" style={{ margin: '12px 1.25rem 0' }}>
            <AlertCircle size={16} /> {error}
          </div>
        ) : null}

        {/* Filter tabs */}
        <div style={{ display: 'flex', gap: 8, padding: '12px 1.25rem 0', flexWrap: 'wrap' }}>
          {FILTER_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => setFilter(tab.key)}
              style={{
                padding: '6px 14px', borderRadius: 99, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                border: `1.5px solid ${filter === tab.key ? '#ea580c' : '#e2e8f0'}`,
                background: filter === tab.key ? '#fff7ed' : '#f8fafc',
                color: filter === tab.key ? '#ea580c' : '#64748b',
              }}
            >
              {tab.label}
              <span style={{
                marginLeft: 6, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 20, height: 20, borderRadius: 99, padding: '0 5px',
                background: filter === tab.key ? '#ea580c' : '#e2e8f0',
                color: filter === tab.key ? '#fff' : '#475569', fontSize: 11, fontWeight: 700,
              }}>
                {counts[tab.key]}
              </span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="content-loading" style={{ padding: '40px 0' }}>
            <div className="loader" />
            <span>Memuat data piutang…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: '48px', textAlign: 'center', color: '#9ca3af' }}>
            <CheckCircle2 size={36} style={{ color: '#22c55e', marginBottom: 8, opacity: 0.7 }} />
            <div style={{ fontWeight: 600 }}>Tidak ada cicilan {filter !== 'ALL' ? 'pada kategori ini' : 'yang outstanding'}</div>
          </div>
        ) : (
          <table className="piutang-table">
            <thead>
              <tr>
                <th>Pasien</th>
                <th>Invoice</th>
                <th>Termin</th>
                <th>Jatuh Tempo</th>
                <th>Nominal</th>
                <th>Sudah Bayar</th>
                <th>Status</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((inst) => {
                const st      = dueStatus(Number(inst.days_overdue || 0));
                const isPaying = payingId === inst.id;
                const isBusy   = busyId === inst.id;
                const phone    = (inst.client_phone || '').replace(/\D/g, '').replace(/^0/, '62');
                return (
                  <Fragment key={inst.id}>
                    <tr>
                      <td>
                        <div style={{ fontWeight: 700 }}>{inst.client_name}</div>
                        <div style={{ fontSize: 12, color: '#6b7280' }}>{inst.client_code}</div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, fontSize: 13 }}>{inst.invoice_number}</div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>
                          Total {rupiah.format(Number(inst.total_transaction_amount))}
                        </div>
                      </td>
                      <td>
                        <div style={{ fontWeight: 600 }}>
                          {inst.installment_number === 1 ? 'DP' : `Cicilan ke-${inst.installment_number}`}
                        </div>
                        <div style={{ fontSize: 11, color: '#9ca3af' }}>
                          {inst.remaining_installments} dari {inst.total_installments} belum lunas
                        </div>
                      </td>
                      <td>
                        {inst.due_date
                          ? new Date(inst.due_date).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' })
                          : '—'}
                      </td>
                      <td style={{ fontWeight: 700 }}>{rupiah.format(Number(inst.amount))}</td>
                      <td style={{ color: '#6b7280' }}>{rupiah.format(Number(inst.total_paid_so_far))}</td>
                      <td>
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 5,
                          padding: '3px 10px', borderRadius: 99, fontSize: 12, fontWeight: 700,
                          background: st.bg, color: st.color,
                        }}>
                          <span style={{ width: 7, height: 7, borderRadius: '50%', background: st.dot, display: 'inline-block' }} />
                          {st.label}
                        </span>
                      </td>
                      <td>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                          <button
                            type="button"
                            onClick={() => { setPayingId(isPaying ? null : inst.id); setPayRef(''); setPayNotes(''); }}
                            disabled={!!busyId}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '5px 10px', borderRadius: 6, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                              background: isPaying ? '#f1f5f9' : '#ea580c',
                              color: isPaying ? '#64748b' : '#fff',
                              border: `1.5px solid ${isPaying ? '#e2e8f0' : '#ea580c'}`,
                            }}
                          >
                            <Banknote size={13} />
                            {isPaying ? 'Batal' : 'Bayar'}
                          </button>
                          {phone ? (
                            <a
                              href={`https://wa.me/${phone}?text=${waMessage(inst)}`}
                              target="_blank"
                              rel="noreferrer"
                              title="Kirim reminder WA"
                              style={{
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                width: 30, height: 30, borderRadius: 6,
                                background: '#dcfce7', color: '#16a34a', textDecoration: 'none', fontSize: 16,
                                border: '1.5px solid #bbf7d0',
                              }}
                            >
                              💬
                            </a>
                          ) : null}
                        </div>
                      </td>
                    </tr>

                    {isPaying ? (
                      <tr key={inst.id + '_pay'} style={{ background: '#fff7ed' }}>
                        <td colSpan={8} style={{ padding: '10px 16px 14px' }}>
                          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <label style={{ fontSize: 11, fontWeight: 700, color: '#7c2d12' }}>METODE BAYAR</label>
                              <select
                                value={payMethod}
                                onChange={(e) => setPayMethod(e.target.value)}
                                disabled={isBusy}
                                style={{ padding: '7px 12px', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 13 }}
                              >
                                {METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                              </select>
                            </div>
                            <div style={{ flex: 1, minWidth: 160, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <label style={{ fontSize: 11, fontWeight: 700, color: '#7c2d12' }}>REFERENSI {payMethod !== 'CASH' ? <span style={{ color: '#dc2626' }}>*</span> : ''}</label>
                              <input
                                value={payRef}
                                onChange={(e) => setPayRef(e.target.value)}
                                placeholder={payMethod !== 'CASH' ? 'No. referensi EDC (wajib)' : 'No. referensi (opsional)'}
                                disabled={isBusy}
                                style={{ padding: '7px 12px', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 13 }}
                              />
                            </div>
                            <div style={{ flex: 2, minWidth: 200, display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <label style={{ fontSize: 11, fontWeight: 700, color: '#7c2d12' }}>CATATAN</label>
                              <input
                                value={payNotes}
                                onChange={(e) => setPayNotes(e.target.value)}
                                placeholder="Catatan pembayaran (opsional)"
                                disabled={isBusy}
                                style={{ padding: '7px 12px', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 13 }}
                              />
                            </div>
                            <button
                              type="button"
                              onClick={() => handlePayInstallment(inst)}
                              disabled={isBusy || (payMethod !== 'CASH' && !payRef.trim())}
                              style={{
                                padding: '8px 18px', borderRadius: 6, fontWeight: 700, fontSize: 13, cursor: 'pointer',
                                background: '#ea580c', color: '#fff', border: 'none',
                                display: 'flex', alignItems: 'center', gap: 6,
                              }}
                            >
                              <Banknote size={15} />
                              {isBusy ? 'Memproses…' : `Konfirmasi ${rupiah.format(Number(inst.amount))}`}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </article>

      <style>{`
        .piutang-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13.5px;
        }
        .piutang-table th {
          text-align: left;
          font-size: 11px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
          color: #7a7364;
          padding: 10px 14px;
          border-bottom: 1.5px solid #e8e2d5;
          background: #fafaf8;
        }
        .piutang-table td {
          padding: 11px 14px;
          border-bottom: 1px solid #f3f0ea;
          vertical-align: top;
        }
        .piutang-table tr:hover td {
          background: #fdfaf5;
        }
        @keyframes spin { to { transform: rotate(360deg); } }
        .spin { animation: spin 0.9s linear infinite; }
      `}</style>
    </div>
  );
}
